import { useCallback, useEffect, useState, useMemo } from 'react';

import { useAuthContext } from '~/shared/context/useAuthContext';
import { isOrganizationView, getOrganizationAccounts } from '~/shared/utils/accountUtils';

import { useOrganizationStatus } from '~/shared/utils/organizationUtils';

import CreateAutoscaleRulesModal from './CreateAutoscaleRulesModal/CreateAutoscaleRulesModal';
import { useAutoscaleRulesBuilder } from './CreateAutoscaleRulesModal/useAutoscaleRulesBuilder';
import AutoscaleRulesFiltersSidePanel from './AutoscaleRulesFiltersSidePanel';
import { useAutoscaleRulesQuery } from './AutoscaleRulesTable/hooks/useAutoscaleRulesQuery';
import AutoscaleRulesTable, { DATA_LIMIT } from './AutoscaleRulesTable/AutoscaleRulesTable';
import AutoscaleRulesEmptyState from './AutoscaleRulesEmptyState';
import AutoscaleRulesHeader from './AutoscaleRulesHeader';
import { useAutoscaleRulesFilters } from './hooks/useAutoscaleRulesFilters';

export default function AutoscaleRulesPage() {
  const { accounts, activeTenantId } = useAuthContext();
  const { isOrganizationAccount } = useOrganizationStatus(accounts, activeTenantId);
  const isOrgView = isOrganizationView(activeTenantId, accounts);

  const [isCreateNewRuleModalOpen, setCreateNewRuleModalOpen] = useState(false);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<{
    ruleId: string;
    rawRule: Record<string, unknown>;
  } | null>(null);
  const [pageOffset, setPageOffset] = useState(0);
  const [lastKnownCount, setLastKnownCount] = useState(0);

  const orgAccounts = useMemo(
    () => getOrganizationAccounts(activeTenantId, accounts, isOrgView),
    [activeTenantId, accounts, isOrgView]
  );

  const builder = useAutoscaleRulesBuilder();

  const {
    filters,
    appliedRuleIDFilters,
    appliedAccountFilters,
    appliedAutoscaleState,
    apiFilters,
    handleResetFilters,
    handleApplyFilters,
  } = useAutoscaleRulesFilters();


  const filtersWithoutAccount = useMemo(() => {
    const { accountIds, ...rest } = filters;
    return rest;
  }, [filters]);

  const apiFiltersWithoutAccount = useMemo(() => {
    if (!apiFilters) return null;

    try {
      const parsed = JSON.parse(apiFilters);
      const filtered = Array.isArray(parsed)
        ? parsed.filter((f) => f.group !== 'accountId')
        : parsed.group !== 'accountId'
          ? parsed
          : null;
      if (!filtered || (Array.isArray(filtered) && filtered.length === 0)) {
        return null;
      }

      return JSON.stringify(filtered);
    } catch {
      return apiFilters;
    }
  }, [apiFilters]);

  useEffect(() => {
    setPageOffset(0);
  }, [filtersWithoutAccount, apiFiltersWithoutAccount, appliedAccountFilters, isOrgView]);

  const { data, isLoading, isError } = useAutoscaleRulesQuery({
    filters: filtersWithoutAccount,
    apiFilters: apiFiltersWithoutAccount,
    dryRun: true,
    page: pageOffset,
    limit: DATA_LIMIT,
    isOrgView,
    appliedAccountFilters,
  });

  useEffect(() => {
    if (data?.count !== undefined) {
      setLastKnownCount(data.count);
    }
  }, [data?.count]);

  const toggleSidePanel = useCallback(() => setSidePanelOpen((o) => !o), []);

  const handleEditRule = useCallback(
    (ruleId: string, rawRule: Record<string, unknown>) => {
      setEditingRule({ ruleId, rawRule });
      builder.loadFromJsonLogic(rawRule);
      setCreateNewRuleModalOpen(true);
    },
    [builder]
  );

  const handleCloseModal = useCallback(() => {
    setCreateNewRuleModalOpen(false);
    // Delay resetting to allow modal close animation to complete
    setTimeout(() => {
      setEditingRule(null);
      builder.resetBuilder();
    }, 300);
  }, [builder]);

  // console.log({ builder });
  // console.log(sidePanelOpen, 'sidePanelOpen');
  // console.log('Rules data:', data);

  const dataCount = isLoading ? lastKnownCount : data?.count;
  const hasNoData = !isLoading && (!data?.rules || data.rules.length === 0);
  const shouldShowEmptyState =
    hasNoData &&
    !(
      (filters.active && filters.active.length > 0) ||
      (filters.ruleIds && filters.ruleIds.length > 0) ||
      (filters.accountIds && filters.accountIds.length > 0)
    ) &&
    !isError;

  return (
    <div className="flex flex-col h-full">
      {!shouldShowEmptyState && (
        <AutoscaleRulesHeader
          rulesCount={data?.count}
          isLoading={isLoading}
          activeFilterCount={(filters.active?.length || 0) + (filters.ruleIds?.length || 0) + (filters.accountIds?.length || 0)}
          onOpenFilters={toggleSidePanel}
          onClearFilters={handleResetFilters}
          onCreateRule={() => setCreateNewRuleModalOpen(true)}
        />
      )}

      <AutoscaleRulesFiltersSidePanel
        open={sidePanelOpen}
        onOpenChange={(open: boolean) => {
          if (sidePanelOpen) {
            setSidePanelOpen(open);
          } else {
            // Fallback: toggle only when change differs to avoid accidental close/open loops
            if (open !== sidePanelOpen) {
              toggleSidePanel();
            }
          }
        }}
        onResetFilters={handleResetFilters}
        onApplyFilters={handleApplyFilters}
        appliedRuleIDFilters={appliedRuleIDFilters}
        appliedAccountFilters={appliedAccountFilters}
        appliedAutoscaleState={appliedAutoscaleState}
        isOrgView={isOrgView}
        accounts={orgAccounts}
        activeOrgId={activeTenantId}
      />

      <div className="flex-1 min-h-0">
        {shouldShowEmptyState ? (
          <div className="py-10 h-full">
            <AutoscaleRulesEmptyState
              onCreateRule={() => setCreateNewRuleModalOpen(true)}
            />
          </div>
        ) : (
          <AutoscaleRulesTable
            loading={isLoading}
            rules={data?.rules || []}
            totalCount={dataCount ?? 0}
            currentPage={Math.floor(pageOffset / DATA_LIMIT)}
            onPageChange={(page) => setPageOffset(page * DATA_LIMIT)}
            onEditRule={handleEditRule}
            isOrganizationAccount={isOrganizationAccount}
          />
        )}
      </div>

      <CreateAutoscaleRulesModal
        isOpen={isCreateNewRuleModalOpen}
        onClose={handleCloseModal}
        builder={builder}
        editMode={!!editingRule}
        ruleId={editingRule?.ruleId}
      />
    </div>
  );
}
